import type {
  DetailsError,
  DetailsErrorCode,
  PortalDetailsMode,
} from './portalDetails';

export interface Customer {
  id: number;
  shopify_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface ShippingAddress {
  id: number | null;
  first_name: string;
  last_name: string;
  address1: string;
  address2: string | null;
  city: string;
  province: string;
  province_code: string | null;
  zip: string;
  country: string;
  country_code: string | null;
  phone: string | null;
}

export interface LineItem {
  id: number;
  product_title: string;
  variant_title: string;
  quantity: number;
  /**
   * Quantity fulfilled in Shopify (#144), supplied by xConnector. The return form caps each line at
   * this value; 0 → not returnable. Optional/null when xConnector doesn't send it → fail open to
   * `quantity`.
   */
  fulfilled_quantity?: number | null;
  returnable_quantity?: number | null;
  price: number;
  total_price: number;
  currency: string;
  image_src: string | null;
  image_width: number | null;
  image_height: number | null;
  tags: string | null;
  total_discount: number;
}

export interface Order {
  id: number;
  shopify_id: number;
  shop_id: number;
  order_name: string;
  total_price: number;
  currency: string;
  delivered_at: string | null;
  shipping_method: string | null;
  payment_method: string | null;
  tags: string | null;
  total_discount: number;
  customer: Customer;
  shipping_address: ShippingAddress;
  delivered_to_pickup_point?: boolean;
  billing_address?: ShippingAddress | null;
  line_items: LineItem[];
}

export interface OrderToken {
  token: string;
  order_details: Order;
  return_context?: {
    recommended_flow?: string;
    has_editable_return?: boolean;
    has_shipped_return?: boolean;
    has_prior_return?: boolean;
  };
}

export interface ExternalToken {
  token: string;
}

export interface Return {
  id: number;
  number: number;
  shop_id: number;
  created_at: string;
  order_id: number;
  order_name: string;
  client_email: string;
  pickup_address: ShippingAddress;
  price: number;
  currency: string;
  awb: string | null;
  iban: string | null;
  return_status: ReturnStatus;
  payment_method: PaymentMethod;
  return_method: ReturnMethod;
  return_method_price: number;
  return_products: ReturnItem[];
  // #105 P4-A: HMAC signature for the "track your return" link. Present only on the create-return
  // response; used to redirect to /return/{id}?sig=... (the signed tracking page).
  tracking_sig?: string;
}

export interface ReturnStatus {
  id: number;
  name: string;
  type: string;
  description: string;
  is_final: boolean;
  order_index: number;
}

// #105 P4-A: redacted "track your return" response returned by the signed-link path of
// GET /returns/{id}. A strict subset of Return with NO PII — no client_email / pickup_address /
// iban / shop_id, and per item no description / return_photo / tags. Mirrors the backend
// ReturnTrackingDto so TypeScript can't imply fields that are never present on the tracking page.
export interface ReturnTracking {
  number: number;
  shopify_return_name: string | null;
  order_name: string;
  created_at: string;
  price: number;
  currency: string;
  return_method_price: number | null;
  awb: string | null;
  return_status: ReturnStatus;
  return_products: ReturnTrackingItem[];
  // #69: the shop of the verified return, used to load its per-shop labels.
  // Absent from an older return-api: the page then keeps the remembered shop.
  shop_id?: number | null;
  // #125 (return-api #265): the display identity of that shop, for the
  // portal header. Absent from an older return-api: caption-only header.
  shop_name?: string | null;
  shop_domain?: string | null;
  // #137 (return-api #288): the carrier drop-off (locker / easybox) code of
  // the return AWB, as stored by the AWB callback; null when there is no
  // usable code. Absent from an older return-api: nothing is rendered.
  locker_code?: string | null;
}

export interface ReturnTrackingItem {
  quantity: number;
  price: number;
  currency: string;
  return_reason: ReturnTrackingReason;
  line_item: ReturnTrackingLineItem;
  exchange_product: ReturnTrackingExchangeProduct | null;
}

export interface ReturnTrackingReason {
  id: number;
  display_name: string;
}

export interface ReturnTrackingLineItem {
  id: number;
  product_title: string;
  variant_title: string;
  sku: string | null;
  image_src: string | null;
  image_width: number | null;
  image_height: number | null;
}

export interface ReturnTrackingExchangeProduct {
  product_title: string;
  variant_title: string;
  image_url: string | null;
}

export interface ExchangeProduct {
  id: number;
  product_title: string;
  variant_title: string;
  price: number;
  currency: string;
  image_url: string;
  total_discount: number;
  product_id: string;
  variant_id: string;
}

export interface ReturnItem {
  description: string;
  quantity: number;
  return_photo: string;
  price: number;
  currency: string;
  return_reason: ReturnReason;
  line_item: LineItem;
  exchange_product: ExchangeProduct | null;
}

export interface ReturnReason {
  id: number;
  name: string;
  display_name: string;
  description: string;
}

export interface ReturnMethod {
  id: number;
  name: string;
  short_instruction: string;
  cost: number;
  display_pickup_address: boolean;
  generate_awb: boolean;
  is_external: boolean;
}

export interface PaymentMethod {
  id: number;
  name: string;
  short_instruction: string;
  require_iban: boolean;
  ask_bank_name?: boolean; // #391
}

export interface ReturnProduct {
  id: number;
  reason: string;
  quantity: number;
  details: string;
  photos: File[];
  requirePhoto: boolean;
  showIndividual: boolean;
  exchangeProduct: ExchangeProduct | null;
  // #496 (review r1 #4): a 400 of POST /returns on this line's text; it
  // travels with the line (split / merge), never by an array index
  detailsError?: DetailsErrorCode;
  // #496 (review r3): the server required text on this line
  // (DESCRIPTION_REQUIRED); persists through Continue / Back until the
  // line's rule inputs change (reason, quantity, split / merge)
  serverRequiresDetails?: boolean;
}

export interface ReturnFullProduct {
  id: number;
  product_title: string;
  variant_title: string;
  quantity: number;
  price: number;
  total_price: number;
  currency: string;
  image_src: string | null;
  image_width: number | null;
  image_height: number | null;
  tags: string | null;
  total_discount: number;
  reason: string;
  quantityReturn: number;
  details: string;
  photos: File[];
  showIndividual: boolean;
  exchangeProduct: ExchangeProduct | null;
  // #496: whether the products step required a photo (the field visibility
  // of PHOTO_ONLY); absent in an older history entry
  requirePhoto?: boolean;
  // #496 (review r3): see ReturnProduct.serverRequiresDetails
  serverRequiresDetails?: boolean;
}

export interface RequirementActionApplied {
  field: string;
  values: string[];
}

export interface RequirementAction {
  type:
    | 'DENY_RETURN'
    | 'EXTEND_DEADLINE'
    | 'SET_RETURN_METHODS'
    | 'REQUIRE_MANUAL_APPROVAL'
    | 'REQUIRE_DEFECT_PHOTO'
    | 'AUTO_APPROVE_RETURN'
    | 'SET_PAYMENT_METHODS';
  applied: RequirementActionApplied[];
}

export interface RequirementFeActionConditionAttribute {
  id: number;
  parent: RequirementFeActionConditionAttribute | null;
  attribute_key: string;
  node_type: string;
  value_type: string;
  deprecated: boolean;
  active: boolean;
}

export interface RequirementFeActionConditionOperator {
  id: number;
  value_type: string;
  code: string;
  label: string;
  input_format: string | null;
}

export interface RequirementFeActionCondition {
  id: number;
  attribute: RequirementFeActionConditionAttribute;
  operator: RequirementFeActionConditionOperator;
  value: string;
}

export interface RequirementFeActionRuleAction {
  id: number;
  action_type:
    | 'DENY_RETURN'
    | 'EXTEND_DEADLINE'
    | 'SET_RETURN_METHODS'
    | 'REQUIRE_MANUAL_APPROVAL'
    | 'REQUIRE_DEFECT_PHOTO';
  action_params: string;
  shop_id: number;
}

export interface RequirementFeAction {
  id: number;
  name: string;
  description: string;
  priority: number;
  active: boolean;
  shop_id: number;
  conditions: RequirementFeActionCondition[];
  rule_action: RequirementFeActionRuleAction;
}

export interface Requirement {
  actions: RequirementAction[];
  fe_actions: RequirementFeAction[];
}

export interface CreateReturnItem {
  order_item_id: number;
  description: string;
  quantity: number;
  photo_keys: string[];
  // #149: omitted (never 0) on a withdrawal return (case C): return-api then
  // uses the shop's system reason
  return_reason_id?: number;
  exchange_product: ExchangeProduct | null;
}

export interface CreateReturn {
  order_id: number;
  order_name: string;
  client_email: string;
  pickup_address: string;
  pickup_address_source?: 'BILLING' | 'CUSTOMER' | 'SHIPPING';
  iban: string | null;
  bank_name: string | null;
  bank_customer_name: string | null;
  payment_method_id: number;
  return_method_id: number;
  return_method_point_id: string | null;
  return_method_point_merchant_connector_id: string | null;
  return_method_point_name: string | null;
  return_products: CreateReturnItem[];
  // #149: the withdrawal declaration this return continues (case C)
  withdrawal_id?: number;
}

export interface PresignedUrlResponse {
  url: string;
  key: string;
}

export interface BankAccount {
  bank: string;
  customer: string;
  iban: string;
}

export interface LocalAction {
  name: string;
  action:
    | 'DENY_RETURN'
    | 'EXTEND_DEADLINE'
    | 'SET_RETURN_METHODS'
    | 'REQUIRE_MANUAL_APPROVAL'
    | 'REQUIRE_DEFECT_PHOTO';
  value: string;
  operator: string;
  // #128: the operator's value type (a NUMBER list compares numbers)
  valueType?: string;
}

export interface Choice {
  value: number;
  label: string;
  name: string;
}

export interface RadioOption {
  id: number;
  label: string;
  value: string;
  cost: number;
  more_details: boolean;
  ask_bank_name?: boolean;
  show_popup: boolean;
}

export interface OfferSession {
  token: string;
  shopId: number;
  orderId: number;
}

export interface LocationState {
  offer_correlation_id?: string | null;
  offer_session?: OfferSession | null;
  order_token: OrderToken;
  requirements: Requirement;
  products_to_return: ReturnProduct[];
  return_reasons: Choice[];
  return_methods: RadioOption[];
  payment_methods: RadioOption[];
  // #72: null on the full-screen QR entry (no storefront proof); `shop` is
  // then the domain verified by return-api
  proof: string | null;
  shop: string;
  allow_exchanges: boolean;
  allow_exchange_more_expensive: boolean;
  // #51: shop's return conditions page; null/empty = no agreement checkbox
  terms_and_conditions_url?: string | null;
  // #72: history entry of a standalone QR session
  standalone_qr?: boolean;
  // #496: the shop's "Detalii" setting (absent = SHOWN / 500, an old API)
  portal_details_mode?: PortalDetailsMode;
  portal_details_max_length?: number;
  // #496: a 400 of POST /returns on the text of a line, shown under it
  details_error?: DetailsError;
  // #496 (review r2 #1): the id of the preferences step's choices, kept in
  // memory (utils/methodsDraft) while the customer corrects the products
  // step; never the choices themselves (bank details) in history state
  methods_draft_id?: string;
  // #149: the return continues a withdrawal declaration (case C)
  withdrawal_id?: number;
  withdrawal_receipt?: WithdrawalReceipt;
}

// #496 (review r1 #2): the choices of the preferences step
export interface MethodsDraft {
  return_method_id?: number;
  payment_method_id?: number;
  return_address?: ShippingAddress;
  bank_account?: BankAccount;
  terms_accepted?: boolean;
}

export interface LocationStateSummary {
  offer_correlation_id?: string | null;
  offer_session?: OfferSession | null;
  order_token: OrderToken;
  return_products: ReturnFullProduct[];
  return_methods: RadioOption[];
  payment_methods: RadioOption[];
  requirements: Requirement;
  return_reasons: Choice[];
  // #72: null on the full-screen QR entry (no storefront proof); `shop` is
  // then the domain verified by return-api
  proof: string | null;
  shop: string;
  allow_exchanges: boolean;
  allow_exchange_more_expensive: boolean;
  // #51: shop's return conditions page; null/empty = no agreement checkbox
  terms_and_conditions_url?: string | null;
  // #72: history entry of a standalone QR session
  standalone_qr?: boolean;
  // #496: the shop's "Detalii" setting (absent = SHOWN / 500, an old API)
  portal_details_mode?: PortalDetailsMode;
  portal_details_max_length?: number;
  // #496 (review r2 #1): the in-memory draft restored by the preferences step
  methods_draft_id?: string;
  // #149: the return continues a withdrawal declaration (case C)
  withdrawal_id?: number;
  withdrawal_receipt?: WithdrawalReceipt;
}

export interface IbanModalProps {
  askBankName?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BankAccount) => void;
  details?: BankAccount;
}

export interface IbanFormProps {
  askBankName?: boolean;
  onSubmit?: (data: BankAccount) => void;
  details?: Partial<BankAccount>;
  title?: string;
  subtitle?: string;
  submitButtonText?: string;
  className?: string;
  showTitle?: boolean;
  onCancel?: () => void;
  showButtons?: boolean;
}

export interface AddressModalProps {
  required?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ShippingAddress) => void;
  address: ShippingAddress;
}

export interface Country {
  code: string;
  name: string;
}

export interface ImagePreview {
  file: File;
  preview: string;
}

export interface JwtToken {
  access_token: string;
  shop: number;
}

export interface OtherSettings {
  id: number;
  days_accept_return: number;
  currency: string;
  allow_exchanges: boolean;
  allow_exchange_more_expensive: boolean;
  terms_and_conditions_url?: string | null;
  // #496 (return-api S1): absent on an older API
  portal_details_mode?: string | null;
  portal_details_max_length?: number | null;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice: {
    amount: string;
    currencyCode: string;
  } | null;
  availableForSale: boolean;
  image: {
    url: string;
    altText: string | null;
  } | null;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  images: {
    edges: Array<{
      node: {
        url: string;
        altText: string | null;
      };
    }>;
  };
  productType: string;
  vendor: string;
  availableForSale: boolean;
  variants: {
    edges: Array<{
      node: ShopifyVariant;
    }>;
  };
}

export interface ProductsResponse {
  data: {
    products: {
      edges: Array<{
        node: ShopifyProduct;
      }>;
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string | null;
      };
    };
  };
}

export interface LocationData {
  LocationId?: string;
  locationId?: string;
  id?: string;
  name?: string;
  description?: string;
  address?: string;
  address1?: string;
  address2?: string;
  zipCode?: string;
  city?: string;
  county?: string;
  countyCode?: string;
  country?: string;
  merchantConnectorId?: string;
  [key: string]: unknown;
}

export const countries: Country[] = [
  { code: 'AD', name: 'Andorra' },
  { code: 'AL', name: 'Albania' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BY', name: 'Belarus' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ES', name: 'Spain' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GE', name: 'Georgia' },
  { code: 'GR', name: 'Greece' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MD', name: 'Moldova' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'RS', name: 'Serbia' },
  { code: 'RU', name: 'Russia' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SM', name: 'San Marino' },
  { code: 'TR', name: 'Turkey' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'XK', name: 'Kosovo' },
];

// #149 / #154: the WITHDRAWAL session the declaration form works with,
// built by the single entry's dispatcher from a WITHDRAWAL-routed lookup
// answer (`order` = its `order_details`)
export interface WithdrawalSession {
  situation?: WithdrawalSituation;
  token: string;
  order: Order;
  prefill_name: string | null;
  prefill_email: string | null;
}

// #154 (return-api #408, docs/withdrawal.md §2.0): the situation preview of
// a withdrawal-routed lookup. Presentation only (which declaration intro is
// shown), never a legal fact: the receipt follows the case classified after
// the declaration is recorded.
export type WithdrawalSituation =
  | 'NOT_SHIPPED'
  | 'SHIPPED'
  | 'UNKNOWN'
  | 'DELIVERED';

// #154: a lookup / QR answer routed to a withdrawal: the lookup body +
// `purpose`, `situation` and the prefills; no return context. The
// proof-less QR adds the verified `shop` (as for a return).
export interface WithdrawalLookup {
  token: string;
  order_details: Order;
  return_context?: null;
  purpose: 'WITHDRAWAL';
  situation?: string | null;
  prefill_name?: string | null;
  prefill_email?: string | null;
}

// #154: what the three lookup calls answer with `X-Portal-Routing: 1`: the
// legacy RETURN body (no `purpose`) or a WITHDRAWAL one
export type LookupAnswer = OrderToken | WithdrawalLookup;

// #149: the withdrawal receipt (`POST /withdrawals` 201, `GET /withdrawals/{id}`)
export interface WithdrawalReceipt {
  id: number;
  order_id: number;
  order_name: string;
  name: string;
  email: string;
  scope: string;
  locale: string;
  declared_at: string;
  time_zone: string;
  state: string;
  case_code: 'A' | 'B' | 'C' | null;
  disposition: string | null;
  next_step:
    | 'PENDING'
    | 'CANCELLATION'
    | 'MERCHANT'
    | 'RETURN_PRODUCTS'
    | string;
  cancel_state: string | null;
  refund_state: string | null;
  return_id: number | null;
  rejected: boolean;
}
